import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

const toneClasses = {
	brand: "bg-brand-primary",
	neutral: "bg-surface-muted",
	danger: "bg-danger",
};

export type BadgeTone = keyof typeof toneClasses;
export const BADGE_TONES = Object.keys(toneClasses) as readonly BadgeTone[];

const sizeClasses = {
	sm: "h-[13px] p-1 text-[10px] font-bold leading-none",
	md: "h-[16px] p-[5px] text-[12px] font-medium leading-none",
	lg: "h-[18px] p-1.5 text-[14px] font-normal leading-none",
};

export type BadgeSize = keyof typeof sizeClasses;
export const BADGE_SIZES = Object.keys(sizeClasses) as readonly BadgeSize[];

const placementClasses = {
	corner: "absolute top-0 right-0 translate-x-1/2 -translate-y-1/2",
	inline: "",
};

export type BadgePlacement = keyof typeof placementClasses;
export const BADGE_PLACEMENTS = Object.keys(placementClasses) as readonly BadgePlacement[];

const badgeVariants = cva(
	"inline-flex w-fit shrink-0 items-center justify-center rounded-full text-inverse",
	{
		variants: {
			tone: toneClasses,
			size: sizeClasses,
			placement: placementClasses,
		},
		defaultVariants: { tone: "brand", size: "md", placement: "corner" },
	},
);

type BadgeVariantProps = VariantProps<typeof badgeVariants>;

type BadgeContentProps = { count?: number; label?: never } | { count?: never; label?: string };

export type BadgeProps = Omit<HTMLAttributes<HTMLSpanElement>, "children"> &
	BadgeContentProps & {
		tone?: NonNullable<BadgeVariantProps["tone"]>;
		size?: NonNullable<BadgeVariantProps["size"]>;
		placement?: NonNullable<BadgeVariantProps["placement"]>;
		testId?: string;
		trackingId?: string;
		ref?: Ref<HTMLSpanElement>;
	};

/**
 * A capped count or verbatim label on a small rounded surface — pinned to a host's corner or
 * inline beside sibling content. Reach for `Tag` when you need a status pill; the two names sit
 * close together.
 */
export const Badge: FC<BadgeProps> = ({
	count,
	label,
	tone,
	size,
	placement,
	testId = "badge",
	trackingId,
	className,
	ref,
	...rest
}) => {
	let content: string;
	if (label) {
		content = label;
	} else if (count === undefined || count <= 0) {
		return null;
	} else {
		content = count > 99 ? "99+" : String(count);
	}

	return (
		<span
			ref={ref}
			className={cn(badgeVariants({ tone, size, placement }), className)}
			data-testid={testId}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{content}
		</span>
	);
};
Badge.displayName = "Badge";
